import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { STORE, waLink } from "@/lib/store";

const NAV = [
  { name: "Home", path: "/" },
  { name: "Bikes & Shop", path: "/bikes" },
  { name: "Gallery", path: "/gallery" },
  { name: "About Us", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#0B0A09]/90 shadow-lg" : "bg-[#0B0A09]"
      } border-b border-white/10`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[70px] items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5 shadow-sm">
              <img src={STORE.logo} alt="Knoble Rides Enterprises logo" className="h-11 w-auto" />
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`navbar-link-${item.name.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-[#8C5A32] text-white" : "text-zinc-200 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={STORE.phoneTel}
              className="flex items-center gap-2 text-sm font-medium text-zinc-200 hover:text-white"
            >
              <Phone className="h-4 w-4 text-[#8C5A32]" />
              {STORE.phoneDisplay}
            </a>
            <a
              href={waLink(`Hello ${STORE.name}, I'd like to enquire about your bikes.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              Chat on WhatsApp
            </a>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center justify-center rounded-lg p-2 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0B0A09] lg:hidden">
          <div className="space-y-1 px-4 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`navbar-mobile-link-${item.name.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) =>
                  `block rounded-lg px-4 py-3 text-base font-semibold ${
                    isActive ? "bg-[#8C5A32] text-white" : "text-zinc-200 hover:bg-white/10"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
            <a
              href={waLink(`Hello ${STORE.name}, I'd like to enquire about your bikes.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa mt-2 block rounded-lg px-4 py-3 text-center text-base font-semibold"
            >
              Chat on WhatsApp
            </a>
            <a href={STORE.phoneTel} className="block px-4 py-3 text-center text-base font-medium text-zinc-200">
              Call {STORE.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
